import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatError } from "@/lib/api";
import { PageHeader, ScopeNotice, useScopeGuard, fmtNum } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Truck, GitFork } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { cliente_id: "", albaran: "", fecha: "", lineas: [{ lote_pt_id: "", cantidad: "" }] };

export default function Expediciones() {
  const { user, scopeTenant } = useAuth();
  const canEdit = ["superadmin", "admin", "calidad", "operario"].includes(user.rol);
  const needsScope = useScopeGuard(user, scopeTenant);

  const [expediciones, setExpediciones] = useState([]);
  const [lotesPT, setLotesPT] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [ajusteDe, setAjusteDe] = useState(null); // expedicion original id
  const [nuevoCli, setNuevoCli] = useState(null); // { nombre, telefono, email } o null

  const load = useCallback(async () => {
    if (needsScope) return;
    const [e, l, c] = await Promise.all([api.get("/expediciones"), api.get("/lotes?tipo=producto"), api.get("/clientes")]);
    setExpediciones(e.data); setLotesPT(l.data); setClientes(c.data);
  }, [needsScope]);
  useEffect(() => { load(); }, [load]);

  const abrirNueva = (originalId = null) => { setForm({ ...EMPTY, lineas: [{ lote_pt_id: "", cantidad: "" }] }); setNuevoCli(null); setAjusteDe(originalId); setOpen(true); };

  const setLinea = (i, patch) => setForm({ ...form, lineas: form.lineas.map((l, idx) => (idx === i ? { ...l, ...patch } : l)) });
  const addLinea = () => setForm({ ...form, lineas: [...form.lineas, { lote_pt_id: "", cantidad: "" }] });
  const rmLinea = (i) => setForm({ ...form, lineas: form.lineas.filter((_, idx) => idx !== i) });

  const crearClienteRapido = async () => {
    if (!nuevoCli?.nombre?.trim()) return toast.error("Nombre del cliente obligatorio");
    try {
      const { data } = await api.post("/clientes", { nombre: nuevoCli.nombre.trim(), telefono: nuevoCli.telefono || null, email: nuevoCli.email || null, pais: "España", certificaciones: [] });
      setClientes([...clientes, data].sort((a, b) => a.nombre.localeCompare(b.nombre)));
      setForm({ ...form, cliente_id: data.id }); setNuevoCli(null);
      toast.success("Cliente creado");
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const guardar = async () => {
    if (!form.cliente_id) return toast.error("El cliente es obligatorio");
    const lineas = form.lineas.filter((l) => l.lote_pt_id && l.cantidad).map((l) => ({ lote_pt_id: l.lote_pt_id, cantidad: Number(l.cantidad) }));
    if (lineas.length === 0) return toast.error("Añada al menos una línea con lote y cantidad");
    const payload = { cliente_id: form.cliente_id, albaran: form.albaran || null, fecha: form.fecha || null, lineas };
    try {
      const url = ajusteDe ? `/expediciones/${ajusteDe}/ajuste` : "/expediciones";
      const { data } = await api.post(url, payload);
      toast.success(`Expedición ${data.codigo} registrada`); setOpen(false); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  if (needsScope) return <ScopeNotice />;

  const ptLabel = (l) => `${l.codigo} · ${l.materia_prima_nombre} (disp. ${fmtNum(l.disponible)})`;

  return (
    <div data-testid="expediciones-page">
      <PageHeader overline="Nivel 3 · Traza" title="Expediciones"
        action={canEdit && <Button onClick={() => abrirNueva()} data-testid="nueva-expedicion-btn" className="bg-[#FF5625] hover:bg-[#E0451A]"><Plus size={16} className="mr-1" />Expedición</Button>} />

      <div className="space-y-3">
        {expediciones.length === 0 && <div className="text-[#0E2A47]/40 py-12 text-center">Aún no hay expediciones.</div>}
        {expediciones.map((e) => (
          <Card key={e.id} className="p-4 border-[#E2E8F0]" data-testid={`expedicion-${e.id}`}>
            <div className="flex items-start justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2 flex-wrap min-w-0">
                <Truck size={18} className="text-[#8B5CF6]" />
                <span className="font-mono font-bold text-[#0E2A47]">{e.codigo}</span>
                {e.tipo === "ajuste" && <Badge variant="outline" className="text-[10px] border-[#F59E0B] text-[#B45309]"><GitFork size={10} className="mr-1" />Ajuste de {e.expedicion_original_codigo}</Badge>}
                {e.ajustes?.length > 0 && <span className="text-xs text-[#B45309]" data-testid={`exp-ajustado-${e.id}`}>Ajustada por {e.ajustes.join(", ")}</span>}
              </div>
              <span className="text-xs text-[#0E2A47]/50">{new Date(e.fecha).toLocaleDateString("es-ES")}</span>
            </div>
            <div className="text-sm text-[#0E2A47]/80 mt-2"><b>Cliente:</b> {e.cliente}{e.albaran ? ` · Albarán: ${e.albaran}` : ""}</div>
            <div className="mt-2 flex flex-wrap gap-2">
              {e.lineas.map((l, i) => (
                <Badge key={i} variant="outline" className="border-[#8B5CF6]/30 text-[#0E2A47]"><span className="font-mono">{l.lote_codigo}</span> · {fmtNum(l.cantidad)} {l.unidad}</Badge>
              ))}
            </div>
            {canEdit && e.tipo !== "ajuste" && (
              <Button size="sm" variant="outline" className="mt-3" onClick={() => abrirNueva(e.id)} data-testid={`exp-ajuste-${e.id}`}><GitFork size={14} className="mr-1" />Expedición de ajuste</Button>
            )}
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-lg" data-testid="expedicion-dialog">
          <DialogHeader><DialogTitle className="text-[#0E2A47]">{ajusteDe ? "Expedición de ajuste" : "Nueva expedición"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-xs font-bold uppercase tracking-wider text-[#0E2A47]/60">Cliente</Label>
                <button type="button" onClick={() => setNuevoCli(nuevoCli ? null : { nombre: "", telefono: "", email: "" })} data-testid="exp-nuevo-cliente-toggle" className="text-xs text-[#FF5625] font-semibold">{nuevoCli ? "Elegir existente" : "+ Nuevo cliente"}</button>
              </div>
              {nuevoCli ? (
                <div className="mt-1.5 rounded-md border border-[#E2E8F0] p-3 space-y-2" data-testid="exp-nuevo-cliente-form">
                  <Input placeholder="Nombre *" value={nuevoCli.nombre} onChange={(e) => setNuevoCli({ ...nuevoCli, nombre: e.target.value })} data-testid="exp-nc-nombre" className="h-9" />
                  <div className="grid grid-cols-2 gap-2">
                    <Input placeholder="Teléfono" value={nuevoCli.telefono} onChange={(e) => setNuevoCli({ ...nuevoCli, telefono: e.target.value })} data-testid="exp-nc-telefono" className="h-9" />
                    <Input placeholder="Email" value={nuevoCli.email} onChange={(e) => setNuevoCli({ ...nuevoCli, email: e.target.value })} data-testid="exp-nc-email" className="h-9" />
                  </div>
                  <Button type="button" size="sm" onClick={crearClienteRapido} data-testid="exp-nc-crear" className="bg-[#10B981] hover:bg-[#0f9d74]">Crear y seleccionar</Button>
                </div>
              ) : (
                <Select value={form.cliente_id} onValueChange={(v) => setForm({ ...form, cliente_id: v })}>
                  <SelectTrigger data-testid="exp-cliente" className="mt-1.5"><SelectValue placeholder="Selecciona cliente…" /></SelectTrigger>
                  <SelectContent className="bg-white max-h-64">{clientes.map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}</SelectContent>
                </Select>
              )}
            </div>
            <div><Label className="text-xs font-bold uppercase tracking-wider text-[#0E2A47]/60">Albarán</Label>
              <Input value={form.albaran} onChange={(e) => setForm({ ...form, albaran: e.target.value })} data-testid="exp-albaran" className="mt-1.5" /></div>
            <div><Label className="text-xs font-bold uppercase tracking-wider text-[#0E2A47]/60">Fecha</Label>
              <Input type="date" value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} data-testid="exp-fecha" className="mt-1.5" /></div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-[#0E2A47]/60">Líneas (lotes PT)</Label>
              <div className="space-y-2 mt-1.5">
                {form.lineas.map((ln, i) => (
                  <div key={i} className="flex flex-wrap sm:flex-nowrap items-center gap-2" data-testid={`exp-linea-${i}`}>
                    <Select value={ln.lote_pt_id} onValueChange={(v) => setLinea(i, { lote_pt_id: v })}>
                      <SelectTrigger data-testid={`exp-lote-${i}`} className="h-9 flex-1 min-w-[160px]"><SelectValue placeholder="Lote PT" /></SelectTrigger>
                      <SelectContent className="bg-white">{lotesPT.map((l) => <SelectItem key={l.id} value={l.id} disabled={l.estado !== "disponible"}>{ptLabel(l)}{l.estado !== "disponible" ? ` · ${l.estado}` : ""}</SelectItem>)}</SelectContent>
                    </Select>
                    <Input type="number" min="0" step="any" placeholder="Cantidad" value={ln.cantidad} onChange={(e) => setLinea(i, { cantidad: e.target.value })} data-testid={`exp-cantidad-${i}`} className="h-9 w-32" />
                    <Button size="icon" variant="ghost" className="h-9 w-9 text-[#EF4444]" onClick={() => rmLinea(i)} data-testid={`exp-rm-${i}`}><Trash2 size={14} /></Button>
                  </div>
                ))}
                <Button size="sm" variant="ghost" className="text-[#3B82F6] h-8" onClick={addLinea} data-testid="exp-add-linea"><Plus size={14} className="mr-1" />Añadir línea</Button>
              </div>
            </div>
          </div>
          <DialogFooter><Button onClick={guardar} data-testid="exp-submit" className="bg-[#0E2A47] hover:bg-[#163C65]">Registrar expedición</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
